import DashboardTile from '../../../components/DashboardTile'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import Subheader from 'app/frontend/base/components/Subheader'
import { useTranslation } from 'react-i18next'
import TeamSwitcher from '../../TeamDashboard/components/TeamSwitcher'
import HeaderTiles from '../../TeamDashboard/components/HeaderTiles'
import TeamSubheader from '../../TeamDashboard/components/TeamSubheader'
import { Link } from 'react-router-dom'
import { useGuild } from 'app/frontend/base/config/context/guild'
import BannerImage from 'app/frontend/base/components/BannerImage'
import { premiumColorForGuild, useBreakpoint } from 'app/frontend/base/helpers'
import { Star } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import PremiumBenefitsModal from '../../TeamDashboard/components/PremiumBenefitsModal'
import { useState } from 'react'
dayjs.extend(RelativeTime)

interface Props {
  uriName: string
  uriRegion: string
  uriRealm: string
}

const GuildHeader = ({ uriName, uriRegion, uriRealm }: Props) => {
  const { guild, guildReference, teamReference } = useGuild()
  const [premiumBenefitsOpen, setPremiumBenefitsOpen] = useState(false)
  const guildInfo = guild || guildReference
  const breakpoint = useBreakpoint()

  const { t } = useTranslation()
  const uriLinkFragment = `${uriRegion}/${uriRealm}/${uriName}`
  const basePath = `${
    guildInfo?.path ? guildInfo.path : `/guild/${uriLinkFragment}`
  }`

  const guildName = guildInfo?.name || uriName.replaceAll('-', ' ')
  const wordCount = guildName.split(' ').length

  return (
    <>
      <header className='border-b-2 -ml-8 -mr-8 max-sm:-ml-4 max-sm:-mr-4'>
        <div className='flex items-center -mt-8 ml-8 mr-8 max-sm:ml-4 max-sm:mr-4'>
          <BannerImage
            className='hidden sm:block mr-4 ml-[-0.7rem]'
            faction={guildInfo?.faction}
            fileName={guildInfo?.bannerFilename}
            size='large'
          />

          <div className='grow mb-2'>
            <div className='flex justify-between items-end'>
              <div className={`flex flex-col mt-2`}>
                <span
                  className={`text-sm text-muted-foreground capitalize h-[1.6rem] leading-6`}
                >
                  {guildInfo?.realm?.nameWithRegion ||
                    `${uriRealm} (${uriRegion.toUpperCase()})`}
                </span>

                <div className='flex items-center gap-3'>
                  <Link to={basePath}>
                    <h1 className='leading-none flex items-center gap-2 sm:mb-2 max-sm:text-xl'>
                      {breakpoint === 'xs' ? (
                        <BannerImage
                          faction={guildInfo?.faction}
                          fileName={guildInfo?.bannerFilename}
                          size='small'
                        />
                      ) : null}

                      <span
                        className={`capitalize ${
                          wordCount === 1 ? 'break-all' : ''
                        } `}
                      >
                        {guildInfo?.name || uriName.replaceAll('-', ' ')}
                      </span>
                    </h1>
                  </Link>

                  {guild?.premiumPoints && guild?.premiumPoints > 0 ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <Star
                          onClick={() => setPremiumBenefitsOpen(true)}
                          className={`w-4 h-4 ${
                            premiumColorForGuild(guild)?.className
                          } sm:mb-1 sm:w-5 sm:h-5`}
                        />
                      </TooltipTrigger>
                      <TooltipContent className='flex flex-col gap-0.5 items-center'>
                        <span
                          className={premiumColorForGuild(guild)?.className}
                        >
                          {premiumColorForGuild(guild)?.tooltip}
                        </span>{' '}
                        <span className='text-muted-foreground text-xs'>
                          {guild?.premiumPoints} boosts applied
                        </span>
                      </TooltipContent>
                    </Tooltip>
                  ) : null}

                  <PremiumBenefitsModal
                    open={premiumBenefitsOpen}
                    setOpen={setPremiumBenefitsOpen}
                  />
                </div>
              </div>

              <div className='flex'>
                {teamReference ? (
                  <HeaderTiles />
                ) : (
                  <>
                    <DashboardTile
                      className='hidden sm:block'
                      innerClassName='h-8'
                      title='members'
                      dataPresent={true}
                    >
                      <div>{guild?.guildMembers || '-'}</div>
                    </DashboardTile>

                    <DashboardTile
                      className='hidden md:block'
                      innerClassName='h-8'
                      title='founded'
                      dataPresent={true}
                    >
                      <div>
                        {guildInfo?.foundedAt
                          ? dayjs(guildInfo?.foundedAt).fromNow()
                          : '-'}
                      </div>
                    </DashboardTile>
                  </>
                )}

                <DashboardTile
                  innerClassName='h-8'
                  title='links'
                  dataPresent={true}
                >
                  <div className='flex gap-2'>
                    {teamReference?.spreadsheetPath ? (
                      <a
                        href={teamReference.spreadsheetPath}
                        target='_blank'
                        rel='noreferrer'
                      >
                        <img
                          src='https://data.wowaudit.com/img/sheets-logo.png'
                          className='w-6 min-w-6'
                        />
                      </a>
                    ) : null}
                    <a
                      href={
                        guildInfo?.blizzardUrl ||
                        `https://worldofwarcraft.blizzard.com/en-gb/guild/${uriLinkFragment}`
                      }
                      target='_blank'
                      rel='noreferrer'
                    >
                      <img
                        src='https://data.wowaudit.com/img/wow-logo.png'
                        className='w-6 min-w-6'
                      />
                    </a>
                    <a
                      href={
                        guildInfo?.wclUrl ||
                        `https://www.warcraftlogs.com/guild/${uriLinkFragment}`
                      }
                      target='_blank'
                      rel='noreferrer'
                    >
                      <img
                        src='https://data.wowaudit.com/img/wcl-logo.png'
                        className='w-6 min-w-6'
                      />
                    </a>
                    <a
                      href={
                        guildInfo?.raiderioUrl ||
                        `https://raider.io/guilds/${uriLinkFragment}`
                      }
                      target='_blank'
                      rel='noreferrer'
                    >
                      <img
                        src='https://data.wowaudit.com/img/raiderio-logo.png'
                        className='w-6 min-w-6'
                      />
                    </a>
                  </div>
                </DashboardTile>
              </div>
            </div>

            <div className='h-[1.6rem] max-sm:mt-1'>
              <TeamSwitcher />
            </div>
          </div>
        </div>
      </header>

      {teamReference ? (
        <TeamSubheader />
      ) : guildInfo?.id ? (
        <Subheader
          menuItems={[
            {
              name: t('shared.overview'),
              path: basePath,
              width: 94,
              exact: true,
            },
            {
              name: t('shared.teams'),
              path: `${basePath}/teams`,
              width: 75,
            },
            {
              name: t('shared.news'),
              path: `${basePath}/news`,
              width: 69,
            },
            {
              name: t('shared.members'),
              path: `${basePath}/members`,
              width: 94,
            },
          ]}
        />
      ) : null}
    </>
  )
}

export default GuildHeader
